<template>
  <Dialog
    v-if="modelValue"
    title="Create credit note"
    :custom-title="true"
    :show-close-button="true"
    :show-footer="false"
    :lg="true"
    :md="false"
    :sm="false"
    custom-width-class="!max-w-[min(64rem,96vw)]"
    :loading="submitting"
    @close-event="close"
  >
    <template #title>
      <div class="flex min-w-0 flex-col gap-0.5 pr-2">
        <h4 class="text-lg font-semibold leading-tight text-gray-900">
          Create credit note
        </h4>
        <p class="text-sm font-medium text-gray-600">
          Order #{{ orderLabel }}
          <template v-if="invoiceLabel">
            · Invoice {{ invoiceLabel }}
          </template>
        </p>
      </div>
    </template>

    <template #modal-content>
      <template v-if="!eligibility">
        <p class="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900">
          Eligibility was not provided to the wizard. The picker should pass it through; check
          <code>SelectStripeInvoiceForCreditNoteModal</code>.
        </p>
      </template>

      <!-- Inline success state (US 5253 / Task 16179): replaces the form with a result card so the
           payment-path-specific copy (especially the finance@ instruction for non-card paid
           invoices) cannot be dismissed accidentally as a toast can. Card-paid never shows
           finance@; non-card always shows finance@; unpaid never mentions refunds. -->
      <template v-else-if="state === 'success' && submitResult">
        <div
          class="rounded-lg border px-4 py-4 shadow-sm"
          :class="successPanelClass"
          role="status"
          aria-live="polite"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-semibold"
              :class="successIconClass"
              aria-hidden="true"
            >
              ✓
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold">
                {{ successHeadline }}
              </h5>
              <p class="mt-1 text-sm leading-relaxed">
                {{ paymentPathOutcomeMessage(submitResult.paymentPath) }}
              </p>
              <dl class="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                <div class="flex items-baseline gap-2">
                  <dt class="font-medium text-current/80">Credit total</dt>
                  <dd class="font-semibold">
                    {{ formatMinor(submitResult.totalMinor) }}
                    {{ submitResult.currency }}
                  </dd>
                </div>
                <div v-if="submitResult.creditNoteId" class="flex items-baseline gap-2 truncate">
                  <dt class="font-medium text-current/80">Stripe credit note</dt>
                  <dd class="truncate font-mono text-[11px]">{{ submitResult.creditNoteId }}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f]"
              @click="onSuccessDone"
            >
              Done
            </button>
          </div>
        </div>
      </template>

      <!-- Inline error state (US 5253 / Task 16179): shows Stripe / backend error message + retry
           button. Refund-failure path adds an actionable note pointing to Stripe; no success copy
           is shown for failed creates (UI never refreshes as if credited — emit only fires on
           genuine success). -->
      <template v-else-if="state === 'error' && submitError">
        <div
          class="rounded-lg border border-rose-200 bg-rose-50/90 px-4 py-4 text-rose-950 shadow-sm"
          role="alert"
          aria-live="assertive"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-200 font-semibold text-rose-900"
              aria-hidden="true"
            >
              !
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold text-rose-950">
                Credit note was not created
              </h5>
              <p class="mt-1 text-sm leading-relaxed text-rose-950/95 break-words">
                {{ submitError.message }}
              </p>
              <details v-if="submitError.detail" class="mt-2 text-xs text-rose-900/80">
                <summary class="cursor-pointer select-none hover:underline">Technical detail</summary>
                <p class="mt-1 break-words font-mono text-[11px]">{{ submitError.detail }}</p>
              </details>
              <p
                v-if="submitError.isRefundFailure"
                class="mt-2 rounded-md border border-rose-300/70 bg-white/80 px-3 py-2 text-xs text-rose-900"
              >
                Stripe rejected the card refund. Verify the original payment in your Stripe
                dashboard — the charge may be too old, already refunded, or the payment source may
                no longer be valid. If a refund is still owed, issue it manually in Stripe.
              </p>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              @click="close"
            >
              Cancel
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-[#219f84] bg-white px-4 py-2 text-sm font-semibold text-[#219f84] shadow-sm transition hover:bg-[#219f84] hover:text-white"
              @click="onErrorRetry"
            >
              Edit &amp; retry
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- Prior credit notes banner — visible when the invoice already has CN(s) against it.
             Full mode would try to credit the whole invoice and run into the cap; the wizard
             defaults to Partial in this case (see watch on props.modelValue / props.eligibility). -->
        <section
          v-if="eligibility.existing_credit_notes.length > 0"
          class="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-950"
          role="note"
        >
          <p class="font-semibold">
            {{ eligibility.existing_credit_notes.length }} prior credit note{{ eligibility.existing_credit_notes.length === 1 ? '' : 's' }} against this invoice
          </p>
          <p class="mt-1 text-xs text-amber-950/95">
            Total already credited:
            <strong>
              {{ formatMinor(priorCreditedMinor) }} {{ eligibility.currency }}
            </strong>
            · Remaining creditable:
            <strong>
              {{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}
            </strong>.
            Switched to <strong>Partial</strong> mode — pick lines / quantities to fit the cap, or switch a line to <strong>By amount</strong> to credit a specific amount.
          </p>
          <details class="mt-2 text-xs" open>
            <summary class="cursor-pointer select-none text-amber-900 hover:underline">
              Credit note history ({{ eligibility.existing_credit_notes.length }})
            </summary>
            <ul class="mt-1.5 space-y-2 pl-1 text-amber-950/95">
              <li
                v-for="cn in eligibility.existing_credit_notes"
                :key="cn.id"
                class="rounded-md border border-amber-200/60 bg-white/70 p-2"
              >
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <span class="font-semibold text-amber-950">
                    {{ formatMinor(cn.amount_minor) }} {{ eligibility.currency }}
                  </span>
                  <span v-if="cn.number" class="text-amber-900">· {{ cn.number }}</span>
                  <span v-if="cn.status" class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                    {{ cn.status }}
                  </span>
                  <span v-if="cn.created" class="text-amber-800/80">· {{ formatCnCreated(cn.created) }}</span>
                </div>
                <div class="mt-0.5 text-[10px]">
                  <code class="rounded bg-amber-100/60 px-1 font-mono text-[10px] text-amber-900">{{ cn.id }}</code>
                  <span v-if="cn.reason" class="ml-2">
                    Reason: <span class="font-medium">{{ formatCnReason(cn.reason) }}</span>
                  </span>
                </div>
                <p v-if="cn.memo" class="mt-1 text-[11px] italic text-amber-900/95">
                  “{{ cn.memo }}”
                </p>
                <a
                  v-if="cn.pdf"
                  :href="cn.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Credit note PDF"
                  class="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                >
                  Open PDF ↗
                </a>
              </li>
            </ul>
          </details>
        </section>

        <!-- Mode toggle -->
        <section class="mb-4">
          <div
            class="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1"
            role="tablist"
            aria-label="Credit note mode"
          >
            <button
              v-for="opt in modeOptions"
              :key="opt.value"
              type="button"
              role="tab"
              :aria-selected="mode === opt.value"
              class="rounded-md px-3 py-1.5 text-sm font-medium transition"
              :class="
                mode === opt.value
                  ? 'bg-white text-[#219f84] shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="switchMode(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <p class="mt-2 text-xs text-slate-500">{{ modeHint }}</p>
        </section>

        <!-- Invoice lines -->
        <section
          v-if="eligibility.lines.length > 0"
          class="mb-5 rounded-lg border border-slate-200 bg-white"
        >
          <header class="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <h5 class="text-sm font-semibold text-slate-900">Invoice lines</h5>
            <div class="flex items-center gap-2">
              <!-- Lives HERE (not in the free-form section) because switching a line to amount
                   entry is an Invoice-lines action — the free-form explainer only explains.
                   Also rendered in Full mode: the handler switches to Partial itself, so this is
                   the one visible amount-entry affordance while the per-row toggles are hidden. -->
              <button
                type="button"
                data-testid="free-form-auto-tax-use-amount-button"
                :disabled="!amountModeAvailable"
                :title="
                  amountModeAvailable
                    ? 'Switch the first eligible line to By amount and focus its Credit amount box'
                    : 'No line on this invoice can take an amount-based credit any more (Stripe basis lock).'
                "
                class="inline-flex items-center gap-1 rounded-md border border-[#219f84] px-2.5 py-1 text-xs font-medium text-[#219f84] transition hover:bg-[#219f84] hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                @click="switchFirstEligibleRowToAmountMode"
              >
                Credit a specific amount
              </button>
              <span class="text-xs text-slate-500">
                {{ eligibility.lines.length }} line{{ eligibility.lines.length === 1 ? '' : 's' }}
              </span>
            </div>
          </header>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[640px] table-fixed border-collapse text-sm">
              <colgroup>
                <col :style="{ width: COLUMN_WIDTHS.select }" />
                <col />
                <col />
                <col />
                <col :style="{ width: COLUMN_WIDTHS.qty }" />
                <col :style="{ width: COLUMN_WIDTHS.amount }" />
                <col :style="{ width: COLUMN_WIDTHS.lineTotal }" />
              </colgroup>
              <thead class="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th class="px-3 py-2 text-left" scope="col">
                    <span class="sr-only">Select</span>
                  </th>
                  <th class="px-3 py-2 text-left" scope="col">Description</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Unit</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Inv. qty</th>
                  <th class="px-3 py-2 text-right" scope="col">Credit qty</th>
                  <th class="px-3 py-2 text-right" scope="col">Credit amount</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Line total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr
                  v-for="row in lineRows"
                  :key="row.id"
                  data-testid="line-row"
                  :data-line-id="row.id"
                  :class="[
                    row.selected ? '' : 'bg-slate-50/60 text-slate-400',
                    row.remainingQty <= 0 ? 'opacity-60' : '',
                  ]"
                >
                  <td class="px-3 py-2 align-middle">
                    <input
                      type="checkbox"
                      :checked="row.selected"
                      :disabled="mode === 'full' || row.remainingQty <= 0"
                      :title="row.remainingQty <= 0 ? 'This line is already fully credited' : undefined"
                      class="h-4 w-4 rounded border-slate-300 text-[#219f84] focus:ring-[#219f84] disabled:cursor-not-allowed"
                      @change="onSelectToggle(row, ($event.target as HTMLInputElement).checked)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <div class="truncate text-slate-800" :class="row.selected ? '' : 'text-slate-400'">
                      {{ row.description || '—' }}
                    </div>
                    <div class="font-mono text-[10px] text-slate-400">{{ row.id }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="mt-0.5 text-[10px] font-medium text-amber-700"
                    >
                      <template v-if="row.remainingQty <= 0">
                        Already fully credited ({{ row.alreadyCreditedQty }} of {{ row.quantity }})
                      </template>
                      <template v-else>
                        {{ row.alreadyCreditedQty }} of {{ row.quantity }} already credited
                      </template>
                    </div>
                    <!-- WS2 — per-row entry mode toggle; hidden in Full mode (everything stays
                         disabled there, same as before this change). -->
                    <div
                      v-if="mode !== 'full'"
                      class="mt-1.5 inline-flex rounded border border-slate-200 bg-slate-50 p-0.5 text-[10px]"
                      role="tablist"
                      :aria-label="`Entry mode for ${row.description || row.id}`"
                    >
                      <button
                        type="button"
                        role="tab"
                        data-testid="line-entry-mode-qty"
                        :aria-selected="row.entryMode === 'qty'"
                        :disabled="!row.selected || row.remainingQty <= 0 || row.lockedBasis === 'amount'"
                        :title="
                          row.lockedBasis === 'amount'
                            ? 'This line was previously credited by amount — Stripe locks its further credits to By amount.'
                            : undefined
                        "
                        class="rounded px-1.5 py-0.5 font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="row.entryMode === 'qty' ? 'bg-white text-[#219f84] shadow-sm' : 'text-slate-500 hover:text-slate-800'"
                        @click="onEntryModeChange(row, 'qty')"
                      >
                        By qty
                      </button>
                      <button
                        type="button"
                        role="tab"
                        data-testid="line-entry-mode-amount"
                        :aria-selected="row.entryMode === 'amount'"
                        :disabled="!row.selected || row.remainingAmountMinor <= 0 || row.lockedBasis === 'quantity'"
                        :title="
                          row.lockedBasis === 'quantity'
                            ? 'This line was previously credited by quantity — Stripe locks its further credits to By qty.'
                            : 'Credit a specific amount for this line instead of whole quantities.'
                        "
                        class="rounded px-1.5 py-0.5 font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="row.entryMode === 'amount' ? 'bg-white text-[#219f84] shadow-sm' : 'text-slate-500 hover:text-slate-800'"
                        @click="onEntryModeChange(row, 'amount')"
                      >
                        By amount
                      </button>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    {{ formatMinor(row.unit_amount_minor) }}
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    <div>{{ row.quantity }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="text-[10px] font-normal text-amber-700"
                    >
                      ({{ row.remainingQty }} left)
                    </div>
                  </td>
                  <td
                    class="px-3 py-2 align-middle"
                    :class="canActivateQtyMode(row) ? 'cursor-pointer' : ''"
                    :title="canActivateQtyMode(row) ? 'Click to switch this line to By qty' : undefined"
                    data-testid="line-qty-cell"
                    @click="onQtyCellClick(row)"
                  >
                    <input
                      type="number"
                      min="0"
                      step="1"
                      data-testid="line-qty-input"
                      :data-line-id="row.id"
                      :disabled="!row.selected || mode === 'full' || row.entryMode === 'amount' || row.remainingQty <= 0"
                      :value="row.creditQty"
                      class="w-full rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                      :class="
                        row.invalid && row.entryMode === 'qty'
                          ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                          : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                      "
                      @input="onQtyInput(row, ($event.target as HTMLInputElement).value)"
                    />
                    <p
                      v-if="row.invalid && row.entryMode === 'qty'"
                      data-testid="line-invalid-message"
                      class="mt-1 text-[10px] leading-tight text-rose-600"
                    >
                      {{ row.invalidMessage }}
                    </p>
                  </td>
                  <td
                    class="px-3 py-2 align-middle"
                    :class="canActivateAmountMode(row) ? 'cursor-pointer' : ''"
                    :title="canActivateAmountMode(row) ? 'Click to credit this line by amount instead' : undefined"
                    data-testid="line-amount-cell"
                    @click="onAmountCellClick(row)"
                  >
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      data-testid="line-amount-input"
                      :data-line-id="row.id"
                      :disabled="!row.selected || mode === 'full' || row.entryMode === 'qty' || row.remainingAmountMinor <= 0"
                      :value="row.creditAmountDisplay"
                      class="w-full rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                      :class="
                        row.invalid && row.entryMode === 'amount'
                          ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                          : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                      "
                      placeholder="—"
                      @input="onAmountInput(row, ($event.target as HTMLInputElement).value)"
                    />
                    <p
                      v-if="row.invalid && row.entryMode === 'amount'"
                      data-testid="line-invalid-message"
                      class="mt-1 text-[10px] leading-tight text-rose-600"
                    >
                      {{ row.invalidMessage }}
                    </p>
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums font-semibold text-slate-800">
                    {{ formatMinor(lineTotalMinor(row)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Free-form lines -->
        <section class="mb-5 rounded-lg border border-slate-200 bg-white">
          <header class="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <div>
              <h5 class="text-sm font-semibold text-slate-900">Free-form lines</h5>
              <p class="text-xs text-slate-500">
                Adjustments that don’t correspond to an existing invoice line. Sent to Stripe as
                <code>custom_line_item</code>.
              </p>
            </div>
            <button
              v-if="!isAutoTaxFreeFormDisabled"
              type="button"
              class="inline-flex items-center gap-1 rounded-md border border-[#219f84] px-2.5 py-1 text-xs font-medium text-[#219f84] transition hover:bg-[#219f84] hover:text-white"
              @click="addFreeFormRow"
            >
              <PlusIcon class="h-3.5 w-3.5" />
              Add line
            </button>
          </header>

          <!-- WS3 — Stripe automatic tax disallows custom_line_items server-side. These three
               sibling branches (auto-tax explainer / table / empty-state) form a single
               v-if / v-else-if / v-else chain in document order. -->
          <div v-if="isAutoTaxFreeFormDisabled" data-testid="free-form-auto-tax-explainer" class="px-3 py-3 text-xs text-slate-600">
            <p>
              Free-form lines aren’t supported on this invoice (Stripe automatic tax). To credit
              an arbitrary amount, enter it as a <strong>Credit amount</strong> against an
              invoice line above — switch the line to <strong>By amount</strong> (or use “Credit a
              specific amount” at the top of the table); tax is added automatically.
            </p>
            <p
              v-if="!amountModeAvailable"
              data-testid="free-form-amount-unavailable-note"
              class="mt-1 text-amber-700"
            >
              Every line on this invoice has already been credited by quantity, and Stripe locks a
              line to the credit basis first used on it — only further <strong>quantity</strong>
              credits are possible here.
            </p>
          </div>
          <div v-else-if="freeFormRows.length > 0" class="overflow-x-auto">
            <table class="w-full min-w-[560px] table-fixed border-collapse text-sm">
              <colgroup>
                <col />
                <col :style="{ width: COLUMN_WIDTHS.qty }" />
                <col :style="{ width: COLUMN_WIDTHS.amount }" />
                <col :style="{ width: COLUMN_WIDTHS.lineTotal }" />
                <col :style="{ width: COLUMN_WIDTHS.remove }" />
              </colgroup>
              <thead class="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th class="px-3 py-2 text-left" scope="col">Description</th>
                  <th class="px-3 py-2 text-right" scope="col">Qty</th>
                  <th class="px-3 py-2 text-right" scope="col">Unit amount</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Line total</th>
                  <th class="px-3 py-2 text-center" scope="col">
                    <span class="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <template v-for="row in freeFormRows" :key="row.uuid">
                  <tr data-testid="free-form-row" :class="row.invalid ? 'bg-rose-50/60' : ''">
                    <td class="px-3 py-2 align-middle">
                      <input
                        :value="row.description"
                        type="text"
                        maxlength="1000"
                        data-testid="free-form-description-input"
                        class="w-full rounded border px-2 py-1 text-sm outline-none focus:ring-1"
                        :class="
                          row.invalid && !row.description.trim()
                            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                            : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                        "
                        placeholder="e.g. Goodwill credit"
                        @input="onFreeFormDescriptionInput(row, ($event.target as HTMLInputElement).value)"
                      />
                    </td>
                    <td class="px-3 py-2 align-middle">
                      <input
                        :value="row.quantity"
                        type="number"
                        min="1"
                        step="1"
                        data-testid="free-form-qty-input"
                        class="w-full rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
                        @input="onFreeFormQtyInput(row, ($event.target as HTMLInputElement).value)"
                      />
                    </td>
                    <td class="px-3 py-2 align-middle">
                      <input
                        :value="row.unitAmountDisplay"
                        type="number"
                        min="0"
                        step="0.01"
                        data-testid="free-form-amount-input"
                        class="w-full rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-1"
                        :class="
                          row.invalid && row.description.trim()
                            ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                            : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                        "
                        placeholder="0.00"
                        @input="onFreeFormAmountInput(row, ($event.target as HTMLInputElement).value)"
                      />
                    </td>
                    <td class="px-3 py-2 text-right align-middle tabular-nums font-semibold text-slate-800">
                      {{ formatMinor(freeFormLineTotalMinor(row)) }}
                    </td>
                    <td class="px-3 py-2 text-center align-middle">
                      <button
                        type="button"
                        class="rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                        aria-label="Remove free-form line"
                        @click="removeFreeFormRow(row.uuid)"
                      >
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  <tr v-if="row.invalid">
                    <td colspan="5" class="px-3 pb-2 pt-0">
                      <p data-testid="free-form-invalid-message" class="text-[10px] leading-tight text-rose-600">
                        {{ row.invalidMessage }}
                      </p>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <p v-else class="px-3 py-3 text-xs text-slate-500">
            No free-form lines yet. Use “Add line” to add an adjustment.
          </p>
        </section>

        <!-- Reason + memo -->
        <section class="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1.5 text-sm text-slate-800">
            <span class="font-medium">Reason <span class="text-xs font-normal text-slate-500">(optional)</span></span>
            <select
              v-model="reason"
              class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
            >
              <option :value="null">— Select —</option>
              <option v-for="r in CREDIT_NOTE_REASONS" :key="r.value" :value="r.value">{{ r.label }}</option>
            </select>
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-slate-800 md:col-span-1">
            <span class="font-medium">Memo <span class="text-xs font-normal text-slate-500">(optional)</span></span>
            <textarea
              v-model="memo"
              rows="2"
              maxlength="5000"
              class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
              placeholder="Notes for the credit note (sent to Stripe)"
            />
            <span class="text-xs text-slate-500">
              The memo prints on the credit note PDF — use it to describe why this credit was issued.
            </span>
          </label>
        </section>

        <!-- Summary + submit -->
        <section
          class="sticky bottom-0 -mx-1 rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-3 backdrop-blur"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span class="text-slate-600">
                Remaining creditable:
                <span class="font-semibold text-slate-900">
                  {{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}
                </span>
              </span>

              <!-- WS1 — preview-backed totals (feature-detected backend). -->
              <template v-if="previewSupported === true">
                <span v-if="preview" data-testid="preview-total" class="text-slate-600">
                  Credit total (incl. tax):
                  <span
                    class="font-semibold"
                    :class="effectiveIsOverCap ? 'text-rose-600' : 'text-slate-900'"
                  >
                    {{ formatMinor(preview.total_minor) }} {{ preview.currency }}
                  </span>
                  <span class="ml-1 text-xs text-slate-500">
                    (excl. tax {{ formatMinor(preview.total_excluding_tax_minor) }} · tax {{ formatMinor(preview.tax_minor) }})
                  </span>
                  <span v-if="previewState === 'pending'" class="ml-1 text-[11px] italic text-slate-400">
                    Updating…
                  </span>
                </span>
                <span v-else data-testid="preview-placeholder" class="text-xs italic text-slate-500">
                  {{ previewState === 'error' ? 'Preview unavailable' : 'Calculating total…' }}
                </span>
              </template>

              <!-- Legacy fallback — pre-preview backend (404 feature-detect) or still detecting. -->
              <span v-else class="text-slate-600">
                Credit note total (excl. tax):
                <span
                  class="font-semibold"
                  :class="isOverCap ? 'text-rose-600' : 'text-slate-900'"
                >
                  {{ formatMinor(totalMinor) }} {{ eligibility.currency }}
                </span>
                <span class="text-xs text-slate-500">(Tax is added automatically by Stripe)</span>
              </span>

              <span
                v-if="paymentPathHint"
                class="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-700"
              >
                {{ paymentPathHint }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                :disabled="submitting"
                @click="close"
              >
                Cancel
              </button>
              <button
                v-if="state !== 'confirming'"
                type="button"
                data-testid="create-credit-note-button"
                class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canSubmit"
                @click="onCreateClick"
              >
                <span
                  v-if="submitting"
                  class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
                />
                Create credit note
              </button>
            </div>
          </div>

          <p
            v-if="previewSupported === true && previewState === 'error'"
            class="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-rose-600"
          >
            <span data-testid="preview-error-message">
              {{ previewErrorInfo?.message ?? 'Could not calculate a preview for this credit note.' }}
            </span>
            <button
              type="button"
              data-testid="preview-retry-button"
              class="rounded border border-rose-300 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 transition hover:bg-rose-50"
              @click="schedulePreview(true)"
            >
              Retry
            </button>
          </p>
          <p v-else-if="effectiveIsOverCap" class="mt-2 text-xs font-medium text-rose-600">
            Credit note total exceeds the invoice’s remaining creditable
            ({{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}).
            Reduce a line or remove a free-form row to proceed.
          </p>
          <p v-else-if="totalMinor === 0" class="mt-2 text-xs text-slate-500">
            Select at least one invoice line or add a free-form row to enable submit.
          </p>

          <!-- WS6 — confirm step for card_refund / out_of_band; unpaid submits directly. -->
          <div
            v-if="state === 'confirming'"
            data-testid="confirm-strip"
            class="mt-3 rounded-md border border-amber-300 bg-amber-50/80 px-3 py-3 text-sm text-amber-950"
          >
            <p data-testid="confirm-statement" class="leading-relaxed">{{ confirmStatement }}</p>
            <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                data-testid="confirm-back-button"
                class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                :disabled="submitting"
                @click="state = 'idle'"
              >
                Back
              </button>
              <button
                type="button"
                data-testid="confirm-submit-button"
                class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canSubmit"
                @click="submit"
              >
                <span
                  v-if="submitting"
                  class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
                />
                {{ confirmButtonLabel }}
              </button>
            </div>
          </div>
        </section>
      </template>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';

import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  CREDIT_NOTE_REASONS,
  type CreditNoteEligibility,
  type CreditNoteErrorInfo,
  type CreditNoteInvoiceLine,
  type CreditNotePreview,
  type CreditNoteReason,
  type LineEntryMode,
  amountToMinorUnits,
  creditNoteErrorFromResponse,
  formatMinorAsDisplay,
  isLikelyRefundFailure,
  minorUnitsToAmount,
  parseQtyInput,
  paymentPathOutcomeMessage,
  paymentPathPreCreateStatement,
  previewCreditNote,
  validateFreeFormEntry,
  validateLineEntry,
} from '@/utils/stripeCreditNote';
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';

type Mode = 'full' | 'partial';
/** WS6 adds `'confirming'` — the inline confirm strip shown before card_refund/out_of_band creates. */
type WizardState = 'idle' | 'confirming' | 'submitting' | 'success' | 'error';
/** WS1 — preview request lifecycle, independent of `WizardState`. */
type PreviewState = 'idle' | 'pending' | 'fresh' | 'error';

/**
 * WS4 — single source of truth for the columns shared between the invoice-lines (mirror) table
 * and the free-form table, rendered via <colgroup>/<col> on both so the "Credit qty" / "Credit
 * amount" / "Line total" columns line up with the free-form table's "Qty" / "Unit amount" /
 * "Line total" columns. Values mirror the tailwind widths (w-8, w-28, w-32, w-10) the columns
 * used previously, kept as one constant so the two tables can't drift apart again.
 */
const COLUMN_WIDTHS = {
  select: '2rem',
  qty: '7rem',
  amount: '8rem',
  lineTotal: '7rem',
  remove: '2.5rem',
} as const;

interface SubmitResult {
  paymentPath: string | null;
  totalMinor: number;
  currency: string;
  creditNoteId: string | null;
  rawCreditRow: Record<string, unknown>;
}

interface SubmitError {
  message: string;
  /** Raw backend `errors.detail`, when present — shown behind a collapsed "Technical detail". */
  detail: string | null;
  isRefundFailure: boolean;
}

interface LineRow {
  id: string;
  description: string | null;
  unit_amount_minor: number;
  quantity: number;
  /** Already credited via prior CNs (sum across `existing_credit_notes`). */
  alreadyCreditedQty: number;
  /** Maximum creditable qty for this row right now (`quantity - alreadyCreditedQty`). */
  remainingQty: number;
  /** Maximum creditable amount in minor units (`amount_minor - already_credited_amount_minor`). */
  remainingAmountMinor: number;
  /**
   * Stripe locks each invoice line to the credit basis first used against it (`credit_basis` from
   * eligibility): a qty-credited line can only ever be credited by qty again, and vice versa.
   * `null` = never credited, both modes allowed. Drives disabled state + tooltip on the toggle.
   */
  lockedBasis: 'quantity' | 'amount' | null;
  selected: boolean;
  /**
   * WS2 — per-row entry mode. Exactly one of qty/amount is live at a time; the other input is
   * disabled and its value is cleared on switch so it can't leak stale state into the payload.
   * Defaults to `'qty'`.
   */
  entryMode: LineEntryMode;
  /** Credit quantity entered by the user. NOT clamped at `remainingQty` — see `invalid`. */
  creditQty: number;
  /** Explicit credit amount in minor units; takes precedence over qty when `entryMode === 'amount'`. */
  creditAmountMinor: number | null;
  /** Display string for the amount input, kept separate from numeric state for editing UX. */
  creditAmountDisplay: string;
  /**
   * Reject-over-cap (WS2): true when the active field's parsed value exceeds this row's
   * remaining cap. The raw input text is never clamped — this flag drives the red border +
   * inline message and blocks submit/preview instead.
   */
  invalid: boolean;
  /** Human message shown under the active input while `invalid` is true. */
  invalidMessage: string | null;
}

interface FreeFormRow {
  uuid: string;
  description: string;
  quantity: number;
  unitAmountMinor: number;
  unitAmountDisplay: string;
  /** True once the user has edited any field on this row — gates validation (see `invalid`). */
  touched: boolean;
  /** True when `touched` and the row is incomplete (empty description or zero/negative total). */
  invalid: boolean;
  /** Human message shown under the row while `invalid` is true. */
  invalidMessage: string | null;
}

const props = defineProps<{
  modelValue: boolean;
  orderId: number;
  /** Local `order_item_invoices.id` — required by the backend create-credit-note endpoint. */
  orderItemInvoiceId: number;
  orderLabel: string | number;
  invoiceLabel: string | null;
  eligibility: CreditNoteEligibility | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'credit-note-created', payload: Record<string, unknown>): void;
}>();

const mode = ref<Mode>('full');
const reason = ref<CreditNoteReason | null>(null);
const memo = ref('');
const lineRows = ref<LineRow[]>([]);
const freeFormRows = ref<FreeFormRow[]>([]);
/** Wizard state machine — drives which surface the template renders. */
const state = ref<WizardState>('idle');
const submitResult = ref<SubmitResult | null>(null);
const submitError = ref<SubmitError | null>(null);
/** Regenerated on wizard open; reused on retry unless the form changed since last attempt. */
const idempotencyKey = ref('');
/** True when the user has edited the form after a submit attempt — drives smart key rotation. */
const formDirtyAfterLastSubmit = ref(false);

// ---------------------------------------------------------------------------
// WS1 — credit note preview. `previewSupported` is feature-detected once per modal open: `null`
// until the first request resolves/404s, `false` permanently for that open on a 404 (legacy
// backend — falls back to the pre-tax footer + old aggregate-cap submit gating), `true` once a
// preview request has succeeded or come back with a real (non-404) error.
// ---------------------------------------------------------------------------
const preview = ref<CreditNotePreview | null>(null);
const previewState = ref<PreviewState>('idle');
const previewErrorInfo = ref<CreditNoteErrorInfo | null>(null);
const previewSupported = ref<boolean | null>(null);
let previewSequence = 0;
let previewDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Submitting view is derived from state — used by the existing template + disabled props. */
const submitting = computed(() => state.value === 'submitting');

const modeOptions: Array<{ value: Mode; label: string }> = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
];

const modeHint = computed(() =>
  mode.value === 'full'
    ? 'Credit every line at its full invoiced quantity. Add free-form rows below for extra adjustments.'
    : 'Pick the lines you want to credit and edit quantity or amount per line. Add free-form rows for adjustments.',
);

const paymentPathHint = computed(() => {
  const path = props.eligibility?.payment_path;
  if (path === 'card_refund') return 'Card refund';
  if (path === 'out_of_band') return 'Out-of-band (finance follow-up)';
  if (path === 'unpaid') return 'Reduces amount due';
  return '';
});

/** Sum of prior CN totals against this invoice — surfaced in the "Prior credit notes" banner. */
const priorCreditedMinor = computed(() => {
  const cns = props.eligibility?.existing_credit_notes ?? [];
  let sum = 0;
  for (const cn of cns) {
    const v = Number(cn.amount_minor ?? 0);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
});

/**
 * Success panel styling shifts to amber for the non-card paid path, where a finance@ follow-up
 * is still required — green would imply the refund completed. Card-refund / unpaid stay green.
 */
const successPanelClass = computed(() => {
  if (submitResult.value?.paymentPath === 'out_of_band') {
    return 'border-amber-200 bg-amber-50/90 text-amber-950';
  }
  return 'border-emerald-200 bg-emerald-50/90 text-emerald-950';
});

const successIconClass = computed(() => {
  if (submitResult.value?.paymentPath === 'out_of_band') {
    return 'bg-amber-200 text-amber-900';
  }
  return 'bg-emerald-200 text-emerald-900';
});

const successHeadline = computed(() => {
  switch (submitResult.value?.paymentPath) {
    case 'card_refund':
      return 'Credit note created & refund issued';
    case 'out_of_band':
      return 'Credit note created — action required';
    case 'unpaid':
      return 'Credit note applied to invoice';
    default:
      return 'Credit note created';
  }
});

function makeUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildLineRow(line: CreditNoteInvoiceLine): LineRow {
  const alreadyCredited = Number(line.already_credited_qty ?? 0);
  const remainingQty = Number(line.remaining_qty ?? line.quantity);
  const remainingAmount = Number(line.remaining_amount_minor ?? line.amount_minor);
  const lockedBasis = line.credit_basis ?? null;
  // Amount-locked lines (Stripe basis lock) can never be credited by qty again — open them in
  // amount mode pre-filled with the remaining amount, mirroring the qty default's "what's left".
  const opensInAmountMode = lockedBasis === 'amount';
  const currency = line.currency || 'USD';
  return {
    id: line.id,
    description: line.description,
    unit_amount_minor: line.unit_amount_minor,
    quantity: line.quantity,
    alreadyCreditedQty: alreadyCredited,
    remainingQty,
    remainingAmountMinor: remainingAmount,
    lockedBasis,
    // Auto-deselect lines with nothing left to credit in their (locked) basis.
    selected: opensInAmountMode ? remainingAmount > 0 : remainingQty > 0,
    // Default entry mode is qty (WS2); pre-fill with REMAINING qty (not original quantity) so
    // the wizard reflects what's left to credit after prior CNs.
    entryMode: opensInAmountMode ? 'amount' : 'qty',
    creditQty: opensInAmountMode ? 0 : remainingQty,
    creditAmountMinor: opensInAmountMode && remainingAmount > 0 ? remainingAmount : null,
    creditAmountDisplay:
      opensInAmountMode && remainingAmount > 0
        ? String(minorUnitsToAmount(remainingAmount, currency))
        : '',
    invalid: false,
    invalidMessage: null,
  };
}

function resetRowsFromEligibility(): void {
  const lines = props.eligibility?.lines ?? [];
  lineRows.value = lines.map(buildLineRow);
  freeFormRows.value = [];
  reason.value = null;
  memo.value = '';
  state.value = 'idle';
  submitResult.value = null;
  submitError.value = null;
  formDirtyAfterLastSubmit.value = false;
  // WS1 — feature-detect the preview endpoint fresh on every open (backend may have been
  // deployed/rolled back between opens); kick off the first preview immediately.
  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
    previewDebounceTimer = null;
  }
  previewSupported.value = null;
  preview.value = null;
  previewState.value = 'idle';
  previewErrorInfo.value = null;
  schedulePreview(true);
}

function switchMode(next: Mode): void {
  if (mode.value === next) return;
  mode.value = next;
  if (next === 'full') {
    // Full mode in the "after-prior-CN" case still means "credit each line at its remaining
    // quantity" — credit-already lines stay unselected so submit cap matches remaining_creditable.
    // Full mode also always uses qty entry (the per-row toggle is hidden while mode === 'full'),
    // EXCEPT amount-locked lines (Stripe basis lock) which can only be credited by amount — those
    // get their full remaining amount instead, so Full still means "everything that's left".
    const currency = props.eligibility?.currency ?? 'USD';
    for (const row of lineRows.value) {
      if (row.lockedBasis === 'amount') {
        row.selected = row.remainingAmountMinor > 0;
        row.entryMode = 'amount';
        row.creditQty = 0;
        row.creditAmountMinor = row.remainingAmountMinor > 0 ? row.remainingAmountMinor : null;
        row.creditAmountDisplay =
          row.remainingAmountMinor > 0
            ? String(minorUnitsToAmount(row.remainingAmountMinor, currency))
            : '';
      } else {
        row.selected = row.remainingQty > 0;
        row.entryMode = 'qty';
        row.creditQty = row.remainingQty;
        row.creditAmountMinor = null;
        row.creditAmountDisplay = '';
      }
      revalidateRow(row);
    }
  }
  markFormDirty();
}

/** Re-runs {@link validateLineEntry} for one row and stores the result on it. */
function revalidateRow(row: LineRow): void {
  const currency = props.eligibility?.currency ?? 'USD';
  const result = validateLineEntry({
    entryMode: row.entryMode,
    selected: row.selected,
    parsedQty: row.creditQty,
    remainingQty: row.remainingQty,
    parsedAmountMinor: row.creditAmountMinor ?? 0,
    remainingAmountMinor: row.remainingAmountMinor,
    currency,
  });
  row.invalid = !result.valid;
  row.invalidMessage = result.message;
}

function onSelectToggle(row: LineRow, checked: boolean): void {
  if (row.remainingQty <= 0) {
    // Fully credited — can't be re-selected (defensive; checkbox is also disabled).
    row.selected = false;
    revalidateRow(row);
    markFormDirty();
    return;
  }
  row.selected = checked;
  if (!checked) {
    // Reset to the row's home mode — amount-locked rows must never re-enter qty mode.
    row.entryMode = row.lockedBasis === 'amount' ? 'amount' : 'qty';
    row.creditQty = 0;
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
  } else if (row.creditQty === 0 && row.entryMode === 'qty') {
    row.creditQty = row.remainingQty;
  }
  revalidateRow(row);
  markFormDirty();
}

/**
 * WS2 — entry mode toggle. Switching modes clears the OTHER field's value (and display, for
 * amount) explicitly so stale state from the inactive mode can never leak into the payload.
 */
function onEntryModeChange(row: LineRow, next: LineEntryMode): void {
  if (row.entryMode === next) return;
  // Stripe basis lock — a line credited by one basis can never switch to the other (defensive;
  // the toggle buttons are also disabled with a tooltip when locked).
  if (row.lockedBasis === 'quantity' && next === 'amount') return;
  if (row.lockedBasis === 'amount' && next === 'qty') return;
  row.entryMode = next;
  if (next === 'qty') {
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
  } else {
    row.creditQty = 0;
  }
  revalidateRow(row);
  markFormDirty();
}

/**
 * Discoverability (AB#21269 follow-up): a disabled qty/amount input reads as "dead" — finance
 * users didn't find the By qty | By amount toggle. When the OTHER mode's input is disabled only
 * because of the current entry mode (not Full mode, not deselected, not capped, not basis-locked),
 * clicking its cell switches the row to that mode and focuses the input.
 */
function canActivateQtyMode(row: LineRow): boolean {
  return (
    mode.value !== 'full' &&
    row.selected &&
    row.entryMode === 'amount' &&
    row.remainingQty > 0 &&
    row.lockedBasis !== 'amount'
  );
}

function canActivateAmountMode(row: LineRow): boolean {
  return (
    mode.value !== 'full' &&
    row.selected &&
    row.entryMode === 'qty' &&
    row.remainingAmountMinor > 0 &&
    row.lockedBasis !== 'quantity'
  );
}

function focusRowInput(testId: 'line-qty-input' | 'line-amount-input', lineId: string): void {
  void nextTick(() => {
    const input = document.querySelector<HTMLInputElement>(
      `[data-testid="${testId}"][data-line-id="${lineId}"]`,
    );
    input?.focus();
  });
}

function onQtyCellClick(row: LineRow): void {
  if (!canActivateQtyMode(row)) return;
  onEntryModeChange(row, 'qty');
  focusRowInput('line-qty-input', row.id);
}

function onAmountCellClick(row: LineRow): void {
  if (!canActivateAmountMode(row)) return;
  onEntryModeChange(row, 'amount');
  focusRowInput('line-amount-input', row.id);
}

function onQtyInput(row: LineRow, raw: string): void {
  // Reject-over-cap (WS2): the raw text stays in the input untouched; parseQtyInput only
  // NaN/empty-string-guards (rounds-to-zero-safe), it never clamps to remainingQty.
  row.creditQty = parseQtyInput(raw);
  revalidateRow(row);
  markFormDirty();
}

function onAmountInput(row: LineRow, raw: string): void {
  row.creditAmountDisplay = raw;
  const trimmed = raw.trim();
  if (trimmed === '') {
    row.creditAmountMinor = null;
    revalidateRow(row);
    markFormDirty();
    return;
  }
  const currency = props.eligibility?.currency ?? 'USD';
  const minor = amountToMinorUnits(Number(raw), currency);
  if (minor <= 0) {
    // Rounds-to-zero fix: a value that parses down to 0 minor units (e.g. "0.001") must not
    // leave a stale-looking display behind while the numeric state is cleared — clear both.
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
    revalidateRow(row);
    markFormDirty();
    return;
  }
  // Reject-over-cap (WS2): NOT clamped to remainingAmountMinor — revalidateRow flags it invalid
  // instead so the user's typed number is never silently rewritten.
  row.creditAmountMinor = minor;
  revalidateRow(row);
  markFormDirty();
}

function addFreeFormRow(): void {
  freeFormRows.value.push({
    uuid: makeUuid(),
    description: '',
    quantity: 1,
    unitAmountMinor: 0,
    unitAmountDisplay: '',
    touched: false,
    invalid: false,
    invalidMessage: null,
  });
  markFormDirty();
}

function removeFreeFormRow(uuid: string): void {
  freeFormRows.value = freeFormRows.value.filter((row) => row.uuid !== uuid);
  markFormDirty();
}

/** Re-runs {@link validateFreeFormEntry} for one free-form row and stores the result on it. */
function revalidateFreeFormRow(row: FreeFormRow): void {
  const result = validateFreeFormEntry({
    touched: row.touched,
    description: row.description,
    totalMinor: freeFormLineTotalMinor(row),
  });
  row.invalid = !result.valid;
  row.invalidMessage = result.message;
}

function onFreeFormDescriptionInput(row: FreeFormRow, raw: string): void {
  row.description = raw;
  row.touched = true;
  revalidateFreeFormRow(row);
  markFormDirty();
}

function onFreeFormQtyInput(row: FreeFormRow, raw: string): void {
  const next = Number(raw);
  row.quantity = Number.isFinite(next) ? Math.max(1, Math.floor(next)) : 1;
  row.touched = true;
  revalidateFreeFormRow(row);
  markFormDirty();
}

function onFreeFormAmountInput(row: FreeFormRow, raw: string): void {
  row.unitAmountDisplay = raw;
  row.touched = true;
  if (raw.trim() === '') {
    row.unitAmountMinor = 0;
    revalidateFreeFormRow(row);
    markFormDirty();
    return;
  }
  const currency = props.eligibility?.currency ?? 'USD';
  row.unitAmountMinor = amountToMinorUnits(Number(raw), currency);
  revalidateFreeFormRow(row);
  markFormDirty();
}

function lineTotalMinor(row: LineRow): number {
  if (!row.selected) return 0;
  if (row.entryMode === 'amount') {
    return row.creditAmountMinor ?? 0;
  }
  // Full mode pre-fills creditQty = remainingQty (via switchMode / buildLineRow), so we just
  // use creditQty consistently across both modes — single source of truth.
  return row.creditQty * row.unit_amount_minor;
}

function freeFormLineTotalMinor(row: FreeFormRow): number {
  return Math.max(0, (row.quantity || 0) * (row.unitAmountMinor || 0));
}

const totalMinor = computed(() => {
  let total = 0;
  for (const row of lineRows.value) total += lineTotalMinor(row);
  for (const row of freeFormRows.value) total += freeFormLineTotalMinor(row);
  return total;
});

/** Legacy aggregate cap check (pre-tax local total vs. `remaining_creditable_minor`) — used
 * verbatim when `previewSupported !== true` (404 feature-detect / still detecting). */
const isOverCap = computed(() => {
  const cap = props.eligibility?.remaining_creditable_minor ?? 0;
  return totalMinor.value > cap;
});

/** WS1 — preview-backed cap check: tax-inclusive preview total vs. the authoritative cap. */
const previewIsOverCap = computed(() => {
  if (!preview.value) return false;
  const cap = props.eligibility?.max_creditable_minor ?? props.eligibility?.remaining_creditable_minor ?? 0;
  return preview.value.total_minor > cap;
});

/** Whichever cap check is active for the current backend (preview-backed vs. legacy). */
const effectiveIsOverCap = computed(() => (
  previewSupported.value === true ? previewIsOverCap.value : isOverCap.value
));

/** Tax-inclusive preview total when available, else the local pre-tax total (legacy fallback). */
const effectiveTotalMinor = computed(() => (
  previewSupported.value === true && preview.value ? preview.value.total_minor : totalMinor.value
));

/** True while any invoice-line row is over its own remaining cap (WS2 reject-over-cap). */
const anyLineRowInvalid = computed(() => lineRows.value.some((row) => row.invalid));
/** True while any touched free-form row is incomplete (empty description / zero total). */
const anyFreeFormRowInvalid = computed(() => freeFormRows.value.some((row) => row.invalid));

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (!props.eligibility) return false;
  if (totalMinor.value <= 0) return false;
  if (anyLineRowInvalid.value) return false;
  if (anyFreeFormRowInvalid.value) return false;
  if (previewSupported.value === true) {
    // WS1 — submit requires a fresh (non-stale, non-error) preview; no pre-tax fallback.
    if (previewState.value !== 'fresh') return false;
    if (previewIsOverCap.value) return false;
    return true;
  }
  // Legacy fallback (older backend / still feature-detecting) — old aggregate-cap gating.
  if (isOverCap.value) return false;
  return true;
});

/** WS3 — free-form lines are unsupported server-side when Stripe automatic tax is on. */
const isAutoTaxFreeFormDisabled = computed(() => props.eligibility?.automatic_tax === true);

/**
 * Whether ANY line can still take an amount-based credit. False when every line is either fully
 * credited or basis-locked to quantity (Stripe locks a line to the credit basis first used on
 * it) — in that case the "Use Credit amount instead" affordance is disabled with an explainer.
 */
const amountModeAvailable = computed(() =>
  lineRows.value.some((row) => row.remainingAmountMinor > 0 && row.lockedBasis !== 'quantity'),
);

/** WS6 — statement shown in the confirm step (card_refund / out_of_band) using the best-known total. */
const confirmStatement = computed(() => {
  if (!props.eligibility) return '';
  return paymentPathPreCreateStatement(
    props.eligibility.payment_path,
    effectiveTotalMinor.value,
    props.eligibility.currency,
    { partiallyPaid: props.eligibility.partially_paid ?? false },
  );
});

const confirmButtonLabel = computed(() => {
  const path = props.eligibility?.payment_path;
  if (path === 'card_refund') {
    return `Confirm refund of ${formatMinor(effectiveTotalMinor.value)} ${props.eligibility?.currency ?? ''}`.trim();
  }
  if (path === 'out_of_band') return 'Confirm out-of-band credit';
  return 'Confirm';
});

function close(): void {
  if (submitting.value) return;
  // If the user dismisses (X / Cancel / backdrop) AFTER a successful create, propagate
  // the credit row so the parent still refreshes — otherwise the order would render with
  // stale data even though the CN exists on Stripe + in our ledger.
  if (state.value === 'success' && submitResult.value) {
    emit('credit-note-created', {
      credit: submitResult.value.rawCreditRow,
      payment_path: submitResult.value.paymentPath,
      credit_note_total_minor: submitResult.value.totalMinor,
    });
  }
  emit('update:modelValue', false);
}

function resolveSubmitMode(): 'full' | 'partial' | 'free_form' | 'mixed' {
  const anyMirror = lineRows.value.some((row) => row.selected && lineTotalMinor(row) > 0);
  const anyFree = freeFormRows.value.some((row) => freeFormLineTotalMinor(row) > 0);
  if (anyMirror && anyFree) return 'mixed';
  if (anyFree && !anyMirror) return 'free_form';
  if (mode.value === 'full') return 'full';
  return 'partial';
}

function buildPayload(): Record<string, unknown> {
  const invoiceLineItems: Array<Record<string, unknown>> = [];
  for (const row of lineRows.value) {
    if (!row.selected) continue;
    const lt = lineTotalMinor(row);
    if (lt <= 0) continue;
    const entry: Record<string, unknown> = { invoice_line_item: row.id };
    if (row.entryMode === 'amount' && row.creditAmountMinor !== null) {
      entry.amount = row.creditAmountMinor;
    } else if (row.creditQty > 0) {
      entry.quantity = row.creditQty;
    }
    invoiceLineItems.push(entry);
  }

  const customLineItems: Array<Record<string, unknown>> = [];
  for (const row of freeFormRows.value) {
    if (freeFormLineTotalMinor(row) <= 0) continue;
    if (!row.description.trim()) continue;
    customLineItems.push({
      description: row.description.trim(),
      quantity: Math.max(1, Math.floor(row.quantity || 1)),
      unit_amount: row.unitAmountMinor,
    });
  }

  return {
    order_item_invoice_id: props.orderItemInvoiceId,
    mode: resolveSubmitMode(),
    invoice_line_items: invoiceLineItems,
    custom_line_items: customLineItems,
    reason: reason.value,
    memo: memo.value.trim() || null,
    idempotency_key: idempotencyKey.value,
  };
}

/** Same shape as {@link buildPayload}, minus `idempotency_key` — the preview endpoint's payload. */
function buildPreviewPayload(): Record<string, unknown> {
  const payload = buildPayload();
  delete payload.idempotency_key;
  return payload;
}

/**
 * WS1 — debounced preview scheduler. `immediate: true` (used on wizard open + Retry) skips the
 * debounce and fires right away. No-ops entirely once a 404 has feature-detected the backend as
 * not supporting preview for this modal-open session.
 */
function schedulePreview(immediate = false): void {
  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
    previewDebounceTimer = null;
  }
  if (previewSupported.value === false) return;
  if (!props.eligibility) return;
  if (totalMinor.value <= 0 || anyLineRowInvalid.value || anyFreeFormRowInvalid.value) {
    // Nothing valid to preview yet — clear any stale preview rather than showing an error.
    preview.value = null;
    previewState.value = 'idle';
    previewErrorInfo.value = null;
    return;
  }
  previewState.value = 'pending';
  if (immediate) {
    void runPreview();
    return;
  }
  previewDebounceTimer = setTimeout(() => {
    void runPreview();
  }, 400);
}

async function runPreview(): Promise<void> {
  if (!props.eligibility) return;
  const mySequence = ++previewSequence;
  const payload = buildPreviewPayload();
  try {
    const result = await previewCreditNote(props.orderId, payload);
    if (mySequence !== previewSequence) return; // Stale — a newer request has already landed.
    previewSupported.value = true;
    preview.value = result;
    previewState.value = 'fresh';
    previewErrorInfo.value = null;
  } catch (err) {
    if (mySequence !== previewSequence) return;
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404 && previewSupported.value === null) {
      // Older backend — feature-detect once per modal open, fall back to the legacy footer.
      previewSupported.value = false;
      preview.value = null;
      previewState.value = 'idle';
      previewErrorInfo.value = null;
      return;
    }
    previewSupported.value = true;
    preview.value = null;
    previewState.value = 'error';
    previewErrorInfo.value = creditNoteErrorFromResponse(err);
  }
}

/** Re-runs the debounced preview whenever the (idempotency-key-excluded) payload shape changes. */
const previewPayloadSignature = computed(() => (
  props.eligibility ? JSON.stringify(buildPreviewPayload()) : ''
));

watch(previewPayloadSignature, () => {
  if (!props.modelValue) return;
  schedulePreview();
});

onUnmounted(() => {
  if (previewDebounceTimer) clearTimeout(previewDebounceTimer);
});

/**
 * WS6 — "Create credit note" click handler. card_refund / out_of_band require an explicit
 * confirm step (refund / off-platform money movement); unpaid (and any other/unknown path)
 * submits directly in one click.
 */
function onCreateClick(): void {
  if (!canSubmit.value) return;
  const path = props.eligibility?.payment_path ?? null;
  if (path === 'card_refund' || path === 'out_of_band') {
    state.value = 'confirming';
    if (previewSupported.value === true && previewState.value !== 'fresh') {
      schedulePreview(true);
    }
    return;
  }
  void submit();
}

/**
 * WS3 — auto-tax explainer action: switches the first line row with creditable amount left
 * into amount mode (selecting it first if needed) and focuses its amount input, so the user
 * can credit an arbitrary total without a (server-rejected) free-form line.
 */
function switchFirstEligibleRowToAmountMode(): void {
  // Basis-locked-to-quantity rows can never take an amount credit (Stripe rule) — skip them.
  const target = lineRows.value.find(
    (row) => row.remainingAmountMinor > 0 && row.lockedBasis !== 'quantity',
  );
  if (!target) return;
  if (!target.selected) target.selected = true;
  if (mode.value === 'full') {
    // Full mode hides the per-row entry-mode toggle — switch to Partial so amount mode applies.
    switchMode('partial');
  }
  onEntryModeChange(target, 'amount');
  markFormDirty();
  void nextTick(() => {
    const input = document.querySelector<HTMLInputElement>(
      `[data-testid="line-amount-input"][data-line-id="${target.id}"]`,
    );
    input?.focus();
  });
}

async function submit(): Promise<void> {
  if (!canSubmit.value || !props.eligibility) return;
  if (props.orderItemInvoiceId <= 0) {
    showToast('Internal: invoice ledger id missing — close the wizard and re-open it.', 'error');
    return;
  }

  // Smart idempotency: rotate key only when the form changed since the last submit attempt.
  // A pure retry of the identical payload keeps the same key so Stripe dedupes (AC: double-
  // click → one CN). After a user edit post-error, a new key signals a genuinely new attempt.
  if (formDirtyAfterLastSubmit.value) {
    idempotencyKey.value = makeUuid();
  }

  state.value = 'submitting';
  try {
    const payload = buildPayload();
    // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`).
    // Shape: `{ message, result: { credit, payment_path, credit_note_total_minor }, errors: [] }`.
    const response = await http.post<{
      message: string;
      result: { credit: Record<string, unknown>; payment_path: string; credit_note_total_minor: number };
    }>(`customer-orders/${props.orderId}/credit-notes`, payload);

    const data = response.data.result;
    const paymentPath = data?.payment_path ?? props.eligibility.payment_path ?? null;
    const totalMinor = Number(data?.credit_note_total_minor ?? 0);
    const credit = (data?.credit ?? {}) as Record<string, unknown>;
    submitResult.value = {
      paymentPath,
      totalMinor: Number.isFinite(totalMinor) ? totalMinor : 0,
      currency: props.eligibility.currency,
      creditNoteId: typeof credit.stripe_credit_note_id === 'string' ? credit.stripe_credit_note_id : null,
      rawCreditRow: credit,
    };
    state.value = 'success';
    formDirtyAfterLastSubmit.value = false;
    // Note: emit('credit-note-created') fires when the user dismisses the success card via Done
    // so the parent doesn't see UI updates while the success card is still on screen.
  } catch (err) {
    const info = creditNoteErrorFromResponse(err);
    const expectedPath = props.eligibility.payment_path;
    submitError.value = {
      message: info.message,
      detail: info.detail,
      // `refund_failed` from the backend is authoritative; keep the message-sniffing heuristic
      // as a fallback for older/unmapped error shapes that still describe a refund failure.
      isRefundFailure: info.code === 'refund_failed' || isLikelyRefundFailure(info.message, expectedPath),
    };
    state.value = 'error';
    formDirtyAfterLastSubmit.value = false;
    // No success toast, no emit — the UI never refreshes as if credited on a failed create
    // (AC: failed create does not refresh UI as if credited).
  }
}

/** Success "Done" → delegate to `close()` so dismiss-via-X / Cancel / Done all propagate identically. */
function onSuccessDone(): void {
  close();
}

/**
 * Error "Edit & retry" → back to idle so the user can adjust before re-submitting.
 * Marks the form dirty so the next submit rotates the idempotency key — otherwise Stripe
 * would return the SAME cached error response for an unchanged-payload retry (idempotency
 * caches failures too). True double-click dedupe on the *first* submit is still preserved
 * via the `state === 'submitting'` + `canSubmit` button-disable guard.
 */
function onErrorRetry(): void {
  state.value = 'idle';
  formDirtyAfterLastSubmit.value = true;
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      idempotencyKey.value = makeUuid();
      resetRowsFromEligibility();
      // Default to Partial mode when the invoice already has credit notes against it.
      // Full mode would re-credit all lines at full qty (pre-tax sum) and exceed the
      // post-prior-credits cap — the user must pick what to credit from what's left.
      const hasPriorCns = (props.eligibility?.existing_credit_notes?.length ?? 0) > 0;
      mode.value = hasPriorCns ? 'partial' : 'full';
    }
  },
  { immediate: true },
);

watch(() => props.eligibility, () => {
  if (props.modelValue) {
    resetRowsFromEligibility();
  }
});

/**
 * Smart idempotency rotation: handlers that mutate user-controlled state call `markFormDirty()`,
 * and reactive fields with v-model (reason, memo) are watched individually. Using explicit hooks
 * avoids the false-positive a `formSignature` watch fires when `resetRowsFromEligibility()`
 * mutates the same state during open / eligibility-prop changes.
 */
function markFormDirty(): void {
  if (state.value === 'submitting') return;
  // WS6 — an edit while the confirm strip is showing invalidates that confirmation (the total
  // it quoted may no longer be accurate); drop back to idle so the user re-triggers it.
  if (state.value === 'confirming') state.value = 'idle';
  formDirtyAfterLastSubmit.value = true;
}

watch(reason, () => markFormDirty());
watch(memo, () => markFormDirty());

function formatMinor(minor: number): string {
  const currency = props.eligibility?.currency ?? 'USD';
  return formatMinorAsDisplay(minor, currency);
}

/** Stripe `created` is a unix-seconds timestamp — render as locale date+time. */
function formatCnCreated(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return '';
  try {
    return new Date(unixSeconds * 1000).toLocaleString();
  } catch {
    return '';
  }
}

/** Map Stripe `reason` enum to label (matches the dropdown in the wizard). */
function formatCnReason(reason: string | null | undefined): string {
  if (!reason) return '';
  const match = CREDIT_NOTE_REASONS.find((r) => r.value === reason);
  return match ? match.label : reason;
}

defineExpose({
  totalMinor,
  isOverCap,
  resolveSubmitMode,
  buildPayload,
  buildPreviewPayload,
  canSubmit,
  anyLineRowInvalid,
  anyFreeFormRowInvalid,
  lineRows,
  freeFormRows,
  state,
  preview,
  previewState,
  previewErrorInfo,
  previewSupported,
  effectiveIsOverCap,
  effectiveTotalMinor,
  isAutoTaxFreeFormDisabled,
  confirmStatement,
  confirmButtonLabel,
  onCreateClick,
  schedulePreview,
  switchFirstEligibleRowToAmountMode,
  amountModeAvailable,
  canActivateQtyMode,
  canActivateAmountMode,
  onQtyCellClick,
  onAmountCellClick,
});
</script>
